import { onChangeSpecialUnderWritingProvisionsState, policySpecialUnderwritingProvisionsField, policySpecialUnderwritingProvisionsTypes, setPolicySpecialUnderwritingProvisionsType } from '../../../../../features/ebam/plicy-data-page/policy-special-underwriting-provisions/policy-special-underwriting-provisions.slice';
import { useDispatch } from 'react-redux';
import PolicyCKEditorTextModal from '../../modal/policy-ck-editor-text-modal';
import { useProposalTypeData } from '../../../../../hooks/proposal/use-proposal-type-data';
import PolicyTabHeader from './policy-tab-header';
import { usePolicyActions } from '../../../../../hooks/actions/use-policy-actions';
import CKEditor from '../../../../../library/ckeditor/ck-editor';
import { usePolicyDataState } from '../../../../../hooks/policy/use-policy-data-state';

export default function PolicySpecialUnderwritingProvision() {

    const dispatch = useDispatch()
    const { 
        PSUPEnrollment,
        PSUPRollOverProvisions,
        PSUPTermination,
        PSUPGenDefinitions,
        PSUPFaceAmountInsurance,
        PSUPPremiumComputation,
        PSUPClaimsProcedure,
        PSUPNonCoverageProvisions,
    } = useProposalTypeData()
    const {
        isPSUPEnrollment,
        policyEnrollment,
        isPSUPRollOverProvisions,
        policyRollOverProvisions,
        isPSUPTerminationOfIndividual,
        policyTerminationOfIndividual,
        isPSUPGeneralDefinitions,
        policyGeneralDefinitions,
        isPSUPFaceAmountOfInsurance,
        policyFaceAmountOfInsurance,
        isPSUPPremiumComputation,
        policyPremiumComputation,
        isPSUPNonCoverageProvisions,
        policyCoverageProvisions,
        isPSUPClaimsProcedure,
        policyClaimsProcedure,
        policySpecialUnderwritingProvisionsType,
    } = usePolicyDataState()
    
    const { 
        handleEditSpecialUnderWritingProvisions, 
        isLoadingUpdatePolicy, 
        handleCancelEditPolicyUnderwritingProvisions 
    } = usePolicyActions()

    const handleCKEditorSave = async () => {
        switch (policySpecialUnderwritingProvisionsType) {
            case "enrollment":
                await handleEditSpecialUnderWritingProvisions('enrollment');
                break;

            case "rollOverProvisions":
                await handleEditSpecialUnderWritingProvisions('rollOverProvisions');
                break;
            
            case "terminationOfIndividual":
                await handleEditSpecialUnderWritingProvisions('terminationOfIndividual');
                break;
            
            case "generalDefinitions":
                await handleEditSpecialUnderWritingProvisions('generalDefinitions');
                break;
            
            case "faceAmountOfInsurance":
                await handleEditSpecialUnderWritingProvisions('faceAmountOfInsurance');
                break;
            
            case "premiumComputation":
                await handleEditSpecialUnderWritingProvisions('premiumComputation');
                break;

            case "nonCoverageProvisions":
                await handleEditSpecialUnderWritingProvisions('nonCoverageProvisions');
                break;
            
            case "claimsProcedure":
                await handleEditSpecialUnderWritingProvisions('claimsProcedure');
                break;

            default:
                break;
        }
    };

    const isCKEditorType = ( type: policySpecialUnderwritingProvisionsTypes ): type is policySpecialUnderwritingProvisionsField => {
        return (
            type === "enrollment" ||
            type === "rollOverProvisions" ||
            type === "terminationOfIndividual" ||
            type === "generalDefinitions" ||
            type === "faceAmountOfInsurance" ||
            type === "premiumComputation" ||
            type === "nonCoverageProvisions" ||
            type === "claimsProcedure"
        );
    };
    
    const getPolicyInformationTitle = ( type: policySpecialUnderwritingProvisionsTypes ): string => {
        switch (type) {
            case "enrollment":
                return "for enrollment";
            case "rollOverProvisions":
                return "for roll-over provisions";
            case "terminationOfIndividual":
                return "for termination of individual insurance";
            case "generalDefinitions":
                return "for general definitions";
            case "faceAmountOfInsurance":
                return "for face amount of insurance";
            case "premiumComputation":
                return "for premium computation";
            case "nonCoverageProvisions":
                return "for none-coverage provisions";
            case "claimsProcedure":
                return "for claims procedure";
            default:
                return "";
        }
    };
    
    const getPolicyInformationValue = ( type: policySpecialUnderwritingProvisionsTypes ): string => {
        switch (type) {
            case "enrollment":
                return policyEnrollment ?? "";
            case "rollOverProvisions":
                return policyRollOverProvisions ?? "";
            case "terminationOfIndividual":
                return policyTerminationOfIndividual ?? "";
            case "generalDefinitions":
                return policyGeneralDefinitions ?? "";
            case "faceAmountOfInsurance":
                return policyFaceAmountOfInsurance ?? "";
            case "premiumComputation":
                return policyPremiumComputation ?? "";
            case "nonCoverageProvisions":
                return policyCoverageProvisions ?? "";
            case "claimsProcedure":
                return policyClaimsProcedure ?? "";
            default:
                return "";
        }
    };

    const onChangeCKEditor = (value: string) => {
        if (!isCKEditorType(policySpecialUnderwritingProvisionsType)) {
            return;
        }
        dispatch(onChangeSpecialUnderWritingProvisionsState({field: policySpecialUnderwritingProvisionsType, value}));
    }

    return (
        <section className="overflow-hidden rounded border border-gray-200 bg-white">
            <PolicyTabHeader
                title='Special Underwriting Provisions'
                subTitle='Provide the applicable underwriting provisions and policy conditions.'
            />
            <div className="py-3 px-4 space-y-4 lg:space-y-5">
                {PSUPEnrollment && (
                    <PolicyCKEditorTextModal
                        title='Enrollment'
                        description='Enter the applicable enrollment provisions and requirements.'
                        isEditing={isPSUPEnrollment}
                        onEdit={() => dispatch(setPolicySpecialUnderwritingProvisionsType("enrollment"))}
                        onCancel={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        onSave={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        value={policyEnrollment}
                    />
                )}
                {PSUPRollOverProvisions && (
                    <PolicyCKEditorTextModal
                        title='Roll-over Provisions'
                        description='Enter the applicable roll-over provisions.'
                        isEditing={isPSUPRollOverProvisions}
                        onEdit={() => dispatch(setPolicySpecialUnderwritingProvisionsType("rollOverProvisions"))}
                        onCancel={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        onSave={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        value={policyRollOverProvisions}
                    />
                )}
                {PSUPTermination && (
                    <PolicyCKEditorTextModal
                        title='Termination of Individual Insurance'
                        description='Enter the conditions and provisions for termination of individual insurance.'
                        isEditing={isPSUPTerminationOfIndividual}
                        onEdit={() => dispatch(setPolicySpecialUnderwritingProvisionsType("terminationOfIndividual"))}
                        onCancel={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        onSave={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        value={policyTerminationOfIndividual}
                    />
                )}
                {PSUPGenDefinitions && (
                    <PolicyCKEditorTextModal
                        title='General Definitions'
                        description='Enter the applicable definitions used throughout the policy.'
                        isEditing={isPSUPGeneralDefinitions}
                        onEdit={() => dispatch(setPolicySpecialUnderwritingProvisionsType("generalDefinitions"))}
                        onCancel={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        onSave={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        value={policyGeneralDefinitions}
                    />
                )}
                {PSUPFaceAmountInsurance && (
                    <PolicyCKEditorTextModal
                        title="Face Amount of Insurance"
                        description="Enter the provisions and guidelines specifying the face amount of insurance applicable under the policy."
                        isEditing={isPSUPFaceAmountOfInsurance}
                        onEdit={() => dispatch(setPolicySpecialUnderwritingProvisionsType("faceAmountOfInsurance"))}
                        onCancel={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        onSave={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        value={policyFaceAmountOfInsurance}
                    />
                )}
                {PSUPPremiumComputation && (
                    <PolicyCKEditorTextModal
                        title="Premium Computation"
                        description="Enter the provisions and guidelines describing how the applicable premium is calculated under the policy."
                        isEditing={isPSUPPremiumComputation}
                        onEdit={() => dispatch(setPolicySpecialUnderwritingProvisionsType("premiumComputation"))}
                        onCancel={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        onSave={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        value={policyPremiumComputation}
                    />
                )}
                {PSUPNonCoverageProvisions && (
                    <PolicyCKEditorTextModal
                        title="None-Coverage Provisions"
                        description="Enter the provisions specifying the conditions, exclusions, and circumstances that are not covered under the policy."
                        isEditing={isPSUPNonCoverageProvisions}
                        onEdit={() => dispatch(setPolicySpecialUnderwritingProvisionsType("nonCoverageProvisions"))}
                        onCancel={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        onSave={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        value={policyCoverageProvisions}
                    />
                )}
                {PSUPClaimsProcedure && (
                    <PolicyCKEditorTextModal
                        title='Claims Procedure'
                        description='Enter the applicable claims procedures and requirements.'
                        isEditing={isPSUPClaimsProcedure}
                        onEdit={() => dispatch(setPolicySpecialUnderwritingProvisionsType("claimsProcedure"))}
                        onCancel={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        onSave={() => dispatch(setPolicySpecialUnderwritingProvisionsType(null))}
                        value={policyClaimsProcedure}
                    />
                )}
            </div>
            <CKEditor
                isOpen={isCKEditorType(policySpecialUnderwritingProvisionsType)}
                title={isCKEditorType(policySpecialUnderwritingProvisionsType) ? getPolicyInformationTitle(policySpecialUnderwritingProvisionsType) : "" }
                value={isCKEditorType(policySpecialUnderwritingProvisionsType) ? getPolicyInformationValue( policySpecialUnderwritingProvisionsType ) : "" }
                onChange={onChangeCKEditor}
                onClose={handleCancelEditPolicyUnderwritingProvisions}
                onSave={handleCKEditorSave}
                isLoading={isLoadingUpdatePolicy}
            />
        </section>
    )
}
